"use client";

import Link from "next/link";
import type { ReactNode } from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { LuGem } from "react-icons/lu";

import { InfoTooltip } from "@/components/shared/info-tooltip";
import { useConfirm } from "@/components/ui/confirm";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import { useConfetti } from "@/hooks/use-confetti";
import {
  SEND_DIAMONDS_LOADING_STATE,
  useSendDiamonds,
} from "@/hooks/use-send-diamonds";
import { useUpvotePost } from "@/hooks-api/use-upvote-post";
import { abbreviateNumber, pluralize } from "@/utils/helpers";
import { cn } from "@/utils/lib";

const MAX_DIAMOND_VALUE_WITHOUT_CONFIRM = 25;

type OnDiamondSendProps = {
  valueUsdCents: number;
  diamondLevel: number;
};

interface DiamondPopoverProps {
  hoverTrigger: ReactNode;
  params:
    | {
        postHash: string;
      }
    | {
        publicKey: string;
      };
  onDiamondSend?: (props: OnDiamondSendProps) => void;
  onDiamondSent?: () => void;
  onDiamondError?: () => void;
}

export const DiamondSlider = ({
  hoverTrigger,
  params,
  onDiamondSend,
  onDiamondSent,
  onDiamondError,
}: DiamondPopoverProps) => {
  const { toast } = useToast();
  const { sendDiamonds, loadingState, DIAMOND_LEVEL_MAP } = useSendDiamonds();
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const { wealth } = useContext(UserContext);
  const isLoading = loadingState !== SEND_DIAMONDS_LOADING_STATE.IDLE;
  const [popupOpen, setPopupOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const { confirm } = useConfirm();
  const ratingRef = useRef<number | null>(null);
  const onDiamondSendRef = useRef<typeof onDiamondSend | null>(null);
  ratingRef.current = hoverRating;
  onDiamondSendRef.current = onDiamondSend;
  const { addConfetti } = useConfetti();
  const { upvotePost } = useUpvotePost();

  useEffect(() => {
    if (loadingState === SEND_DIAMONDS_LOADING_STATE.AUTHORIZING) {
      if (ratingRef.current === null) return;

      onDiamondSendRef.current?.({
        valueUsdCents: DIAMOND_LEVEL_MAP[ratingRef.current + 1] * 100,
        diamondLevel: ratingRef.current,
      });
    } else if (loadingState === SEND_DIAMONDS_LOADING_STATE.IDLE) {
      setHoverRating(null);
    }
    // We only want to trigger this effect on loadingState changes
    // So to avoid dep issues, we're using refs for certain values
  }, [loadingState]);

  const selectedRating = null;

  const handleMouseEnter = (index: number) => {
    if (!isLoading) {
      setHoverRating(index);
    }
  };

  const handleMouseLeave = () => {
    if (!isLoading) {
      setHoverRating(null);
    }
  };

  const handleClick = async (index: number) => {
    if ((!selectedRating || selectedRating >= index) && !isLoading) {
      const diamondLevel = index + 1;
      const diamondValue = DIAMOND_LEVEL_MAP[diamondLevel];

      const confirmed =
        diamondValue <= MAX_DIAMOND_VALUE_WITHOUT_CONFIRM ||
        (await confirm({
          description: `Are you sure you want to send Diamonds with a value of $${diamondValue}?`,
        }));

      if (!confirmed) {
        return;
      }

      setPopupOpen(false);

      addConfetti([{ emojis: ["💎"] }]);

      try {
        // @ts-expect-error Overloads are tricky, but everything's fine...
        const postHash = params?.postHash;

        // @ts-expect-error Overloads are tricky, but everything's fine...
        const sendDiamondsSuccess = await sendDiamonds({
          diamondLevel,
          ...params,
        });

        if (postHash && sendDiamondsSuccess) {
          await upvotePost({ postHash }).catch(() => {});
        }

        if (sendDiamondsSuccess) {
          onDiamondSent?.();
        } else {
          onDiamondError?.();
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: `An error happened when sending ${diamondLevel} ${pluralize(diamondLevel, "diamond")}`,
          details: createToastErrorDetails(error),
        });
        console.error("Error sending diamonds", error);
        onDiamondError?.();
      } finally {
        setHoverRating(null);
      }
    }
  };

  const renderDiamond = (tip: number, index: number) => {
    const isHighlighted = hoverRating !== null && index <= hoverRating;
    const isSelected = selectedRating !== null && index <= selectedRating;
    const requiredCents = DIAMOND_LEVEL_MAP[index + 1] * 100;
    const isEnoughBalanceToSendDiamonds = Object.values(wealth || {}).some(
      (num) => num >= requiredCents,
    );

    const diamondStyle = cn(
      `
        relative inline-block animate-bump-fade-in cursor-pointer list-none px-0
        text-[28px] opacity-0 transition-all fill-mode-forwards
        sm:px-1 sm:text-3xl
      `,
      {
        "text-primary":
          isSelected ||
          (isEnoughBalanceToSendDiamonds && (isHighlighted || isSelected)),
        "text-muted":
          (!isHighlighted && !isSelected) || !isEnoughBalanceToSendDiamonds,
        "text-black/20 dark:text-white/20":
          !isEnoughBalanceToSendDiamonds && (isHighlighted || isSelected),
      },
    );

    const content = (
      <li
        style={{ animationDelay: `${index * 0.05}s` }}
        className={diamondStyle}
        onMouseEnter={() => handleMouseEnter(index)}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => {
          e.stopPropagation();

          if (isEnoughBalanceToSendDiamonds) {
            handleClick(index);
          }
        }}
      >
        <div
          className={cn(
            `
              flex origin-bottom flex-col items-center gap-0 text-center
              transition-all
            `,
            {
              "hover:scale-[1.15] sm:hover:scale-[1.2]":
                isEnoughBalanceToSendDiamonds,
            },
          )}
        >
          <span className="text-xs/4 lowercase sm:text-[12px]/5">
            {abbreviateNumber(tip, {
              style: "currency",
              currency: "USD",
              currencyDisplay: "narrowSymbol",
              maximumFractionDigits: index < 2 ? 2 : 0,
            })}
          </span>
          <LuGem />
        </div>
      </li>
    );

    const Diamond = (
      <div key={index}>
        {!isEnoughBalanceToSendDiamonds ? (
          <InfoTooltip
            text={
              <div className="text-center shadow-lg">
                <p>Not enough funds!</p>
                <p>
                  <Link className="font-bold text-primary" href="/wallet">
                    Top-Up
                  </Link>{" "}
                  your wallet.
                </p>
              </div>
            }
          >
            {content}
          </InfoTooltip>
        ) : (
          content
        )}
      </div>
    );

    return Diamond;
  };

  return (
    <div>
      <Popover
        modal={modal}
        open={popupOpen}
        onOpenChange={(open) => {
          setPopupOpen(open);
          if (!open) {
            setModal(false);
          }
        }}
      >
        <PopoverTrigger
          asChild
          openOnHover
          delay={0}
          closeDelay={250}
          onPointerDown={(ev) => {
            setModal(ev.pointerType !== "mouse");
          }}
        >
          {hoverTrigger}
        </PopoverTrigger>
        <PopoverContent
          side="top"
          className="
            flex w-full max-w-[100vw] items-center justify-center rounded-2xl
            bg-white p-4
            dark:bg-black
          "
        >
          <div className="flex items-center justify-center">
            <ul
              className={`
                flex flex-row flex-nowrap items-center justify-center gap-1.5
                sm:gap-2
              `}
            >
              {Object.values(DIAMOND_LEVEL_MAP).map(renderDiamond)}
            </ul>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
